/**
 * Session Cleanup Utility
 *
 * Centralized function to properly clear all session-related data
 * when a user logs out or authentication fails.
 *
 * SECURITY: Ensures complete session isolation between users.
 */

/**
 * Clear all authentication and session data, then create a new guest session.
 *
 * This function should be called:
 * - On logout
 * - On authentication failure (401/403)
 * - When switching between users
 *
 * @returns The new guest session ID
 */
export function clearSessionAndCreateGuest(): string {
  // Clear authentication data
  localStorage.removeItem("auth_token");
  localStorage.removeItem("user_data");

  // SECURITY FIX: Clear old session and create new guest session
  // This prevents logged-out users from seeing previous user's files
  localStorage.removeItem("session_id");
  const newGuestSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem("session_id", newGuestSession);

  // Clear session-related stats and caches
  // This ensures usage counters reset for the new guest session
  localStorage.removeItem("sessionStartTime");
  localStorage.removeItem("session_id_created");
  localStorage.removeItem("lastConversionTime");

  // Clear any cached usage data
  localStorage.removeItem("guestUsageCache");
  localStorage.removeItem("dailyUsageCache");
  localStorage.removeItem("monthlyUsageCache");

  // Clear any file-related caches
  localStorage.removeItem("fileCountCache");
  localStorage.removeItem("recentFilesCache");

  console.log(
    "✅ Session cleared, new guest session created:",
    newGuestSession,
  );

  return newGuestSession;
}

/**
 * Clear only the session (not auth token).
 * Use this when you want to keep the user logged in but reset their session.
 */
export function clearSessionOnly(): string {
  localStorage.removeItem("session_id");
  const newSession = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem("session_id", newSession);

  // Clear session stats
  localStorage.removeItem("sessionStartTime");
  localStorage.removeItem("session_id_created");
  localStorage.removeItem("lastConversionTime");

  return newSession;
}

/**
 * Get or create a session ID.
 * This ensures every user (guest or authenticated) has a session.
 */
export function ensureSessionId(): string {
  let sessionId = localStorage.getItem("session_id");

  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("session_id", sessionId);
    localStorage.setItem("sessionStartTime", Date.now().toString());
  }

  return sessionId;
}

/**
 * Check if the current session is authenticated
 */
export function isAuthenticated(): boolean {
  return !!localStorage.getItem("auth_token");
}

/**
 * Get current user data from localStorage
 */
export function getCurrentUser(): any {
  try {
    const userData = localStorage.getItem("user_data");
    return userData ? JSON.parse(userData) : null;
  } catch {
    return null;
  }
}
